import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  FileText, Download, RefreshCw, Loader2, Users, Shield, Briefcase,
  Mail, Newspaper, CalendarDays, TrendingUp, Building2, ChevronDown, ChevronRight,
} from 'lucide-react';

/**
 * AdminReportsTab
 * --------------------------------------------------------------
 * Single-tab reporting dashboard. Lists every available report grouped by
 * category. Each report is collapsible — clicking expands and lazy-loads the
 * full data. Each report has an "Export CSV" button that hits the same
 * endpoint with `?format=csv` and triggers a file download.
 */
const CATEGORY_ICONS = {
  Agents:     Users,
  Compliance: Shield,
  Pipeline:   TrendingUp,
  Marketing:  Newspaper,
};

const ENDPOINT_BY_KEY = {
  agent_roster:        '/api/admin/reports/agent-roster',
  new_agents_by_year:  '/api/admin/reports/new-agents-by-year',
  agents_by_license:   '/api/admin/reports/agents-by-license',
  agents_by_province:  '/api/admin/reports/agents-by-province',
  agent_activity:      '/api/admin/reports/agent-activity',
  agent_documents:     '/api/admin/reports/agent-documents',
  compliance_cases:    '/api/admin/reports/compliance-cases',
  client_folders:      '/api/admin/reports/client-folders',
  quote_requests:      '/api/admin/reports/quote-requests',
  referrals:           '/api/admin/reports/referrals',
  referrers:           '/api/admin/reports/referrers',
  inquiries:           '/api/admin/reports/inquiries',
  newsletter:          '/api/admin/reports/newsletter',
  calendar_broadcasts: '/api/admin/reports/calendar-broadcasts',
  content:             '/api/admin/reports/content',
};

export default function AdminReportsTab({ apiUrl, adminPassword }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [dataByKey, setDataByKey] = useState({});
  const [loadingByKey, setLoadingByKey] = useState({});

  const loadIndex = useCallback(async () => {
    if (!adminPassword) return;
    setLoading(true); setError('');
    try {
      const r = await fetch(`${apiUrl}/api/admin/reports`, { headers: { 'X-Admin-Password': adminPassword } });
      if (!r.ok) throw new Error(`Failed to load reports index (${r.status})`);
      const d = await r.json();
      setReports(d.reports || []);
    } catch (e) {
      setError(e.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, adminPassword]);

  useEffect(() => { loadIndex(); }, [loadIndex]);

  const loadReport = useCallback(async (key) => {
    const url = ENDPOINT_BY_KEY[key];
    if (!url) return;
    setLoadingByKey(p => ({ ...p, [key]: true }));
    try {
      const r = await fetch(`${apiUrl}${url}`, { headers: { 'X-Admin-Password': adminPassword } });
      if (!r.ok) throw new Error(`Failed to load (${r.status})`);
      const d = await r.json();
      setDataByKey(p => ({ ...p, [key]: d }));
    } catch (e) {
      setDataByKey(p => ({ ...p, [key]: { __error: e.message || 'Failed to load' } }));
    } finally {
      setLoadingByKey(p => ({ ...p, [key]: false }));
    }
  }, [apiUrl, adminPassword]);

  const toggle = (key) => {
    const willOpen = !expanded[key];
    setExpanded(p => ({ ...p, [key]: willOpen }));
    if (willOpen && !dataByKey[key]) loadReport(key);
  };

  const downloadExport = async (key, fmt) => {
    const url = ENDPOINT_BY_KEY[key];
    if (!url) return;
    try {
      const r = await fetch(`${apiUrl}${url}?format=${fmt}`, { headers: { 'X-Admin-Password': adminPassword } });
      if (!r.ok) throw new Error(`Download failed (${r.status})`);
      const blob = await r.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `${key}_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      alert(e.message || 'Download failed');
    }
  };

  const grouped = useMemo(() => {
    const out = {};
    for (const r of reports) (out[r.category] = out[r.category] || []).push(r);
    return out;
  }, [reports]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading reports…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-reports-tab">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-500" />
              Admin Reports
              <span className="bg-indigo-50 text-indigo-700 text-xs font-semibold px-2 py-0.5 rounded-full ml-1">{reports.length}</span>
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => { setDataByKey({}); loadIndex(); }} data-testid="reports-refresh">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Click any report to expand its data. Use the <b>Export CSV</b> button to download a spreadsheet for record-keeping or external analysis.
          </p>
        </CardHeader>
        <CardContent>
          {error && <div className="bg-red-50 border-l-4 border-red-500 text-red-900 p-3 rounded mb-4 text-sm">{error}</div>}
          <div className="space-y-6">
            {Object.keys(grouped).map(cat => {
              const Icon = CATEGORY_ICONS[cat] || FileText;
              return (
                <div key={cat}>
                  <h3 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2 border-b pb-1">
                    <Icon className="h-4 w-4 text-indigo-500" /> {cat}
                  </h3>
                  <div className="space-y-2">
                    {grouped[cat].map(r => (
                      <ReportCard
                        key={r.key}
                        report={r}
                        expanded={!!expanded[r.key]}
                        data={dataByKey[r.key]}
                        loading={!!loadingByKey[r.key]}
                        onToggle={() => toggle(r.key)}
                        onDownloadCsv={() => downloadExport(r.key, 'csv')}
                        onDownloadPdf={() => downloadExport(r.key, 'pdf')}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
// ReportCard
// ============================================================
function ReportCard({ report, expanded, data, loading, onToggle, onDownloadCsv, onDownloadPdf }) {
  return (
    <div className="border rounded-lg bg-white overflow-hidden" data-testid={`report-card-${report.key}`}>
      <div className="p-3 flex items-center gap-3">
        <button
          onClick={onToggle}
          className="flex-1 flex items-center gap-2 text-left"
          data-testid={`report-toggle-${report.key}`}
        >
          {expanded ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
          <div>
            <h4 className="font-semibold text-gray-800 text-sm">{report.label}</h4>
            <p className="text-xs text-gray-500">{report.description}</p>
          </div>
        </button>
        <Button size="sm" variant="outline" onClick={onDownloadCsv} data-testid={`report-csv-${report.key}`} title="Export CSV">
          <Download className="h-3.5 w-3.5 mr-1" /> CSV
        </Button>
        <Button size="sm" variant="outline" onClick={onDownloadPdf} data-testid={`report-pdf-${report.key}`} title="Export PDF" className="text-red-600 border-red-300 hover:bg-red-50">
          <FileText className="h-3.5 w-3.5 mr-1" /> PDF
        </Button>
      </div>
      {expanded && (
        <div className="border-t bg-gray-50 p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : data?.__error ? (
            <div className="text-sm text-red-700 bg-red-50 p-2 rounded border border-red-200">{data.__error}</div>
          ) : (
            <ReportBody reportKey={report.key} data={data} />
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// ReportBody — renders the right shape for each report
// ============================================================
function ReportBody({ reportKey, data }) {
  if (!data) return <p className="text-sm text-gray-500">No data.</p>;
  switch (reportKey) {
    case 'agent_roster':        return <AgentRosterTable rows={data.rows} count={data.count} />;
    case 'new_agents_by_year':  return <NewAgentsTable rows={data.rows} total={data.total} />;
    case 'agents_by_license':   return <GroupedAgentsTable summary={data.summary} groupKey="license" />;
    case 'agents_by_province':  return <GroupedAgentsTable summary={data.summary} groupKey="provinces" />;
    case 'agent_activity':      return <AgentActivityTable counts={data.counts} rows={data.rows} />;
    case 'agent_documents':     return <AgentDocsTable summary={data.summary} rows={data.rows} />;
    case 'compliance_cases':    return <ComplianceCasesTable per_agent={data.per_agent} status_totals={data.status_totals} total={data.total_cases} />;
    case 'client_folders':      return <ClientFoldersTable totals={data.totals} rows={data.rows} />;
    case 'quote_requests':      return <PipelineSummary data={data} kind="quote" />;
    case 'referrals':           return <PipelineSummary data={data} kind="referral" />;
    case 'referrers':           return <ReferrersTable rows={data.rows} total_referrers={data.total_referrers} total_referrals={data.total_referrals} />;
    case 'inquiries':           return <PipelineSummary data={data} kind="inquiry" />;
    case 'newsletter':          return <NewsletterTable rows={data.rows} total={data.total} by_month={data.by_month} />;
    case 'calendar_broadcasts': return <CalendarBroadcastsTable rows={data.rows} by_audience={data.by_audience} by_month={data.by_month} total={data.total} />;
    case 'content':             return <ContentReport data={data} />;
    default:                    return <pre className="text-xs overflow-x-auto">{JSON.stringify(data, null, 2)}</pre>;
  }
}

// ----- Reusable bits ----------------------------------------------
function Stat({ label, value, color = 'gray' }) {
  const cls = {
    gray:    'bg-gray-50 text-gray-800 border-gray-200',
    blue:    'bg-blue-50 text-blue-800 border-blue-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    amber:   'bg-amber-50 text-amber-800 border-amber-200',
    red:     'bg-red-50 text-red-800 border-red-200',
    indigo:  'bg-indigo-50 text-indigo-800 border-indigo-200',
  }[color] || 'bg-gray-50 text-gray-800 border-gray-200';
  return (
    <div className={`border rounded p-2 ${cls}`}>
      <div className="text-xs font-medium opacity-80">{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
}

function ScrollTable({ children, testid }) {
  return (
    <div className="overflow-x-auto border rounded bg-white" data-testid={testid}>
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}

function Th({ children, className = '' }) { return <th className={`text-left text-xs font-semibold text-gray-600 px-3 py-2 border-b ${className}`}>{children}</th>; }
function Td({ children, className = '' }) { return <td className={`px-3 py-2 border-b text-gray-700 ${className}`}>{children}</td>; }

// ----- Per-report renderers --------------------------------------
function AgentRosterTable({ rows = [], count = 0 }) {
  return (
    <div>
      <p className="text-xs text-gray-600 mb-2">{count} agent{count === 1 ? '' : 's'} in the roster.</p>
      <ScrollTable testid="agent-roster-table">
        <thead><tr>
          <Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Role</Th><Th>Licenses</Th><Th>Province</Th><Th>Active</Th><Th>Last login</Th>
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="hover:bg-gray-50">
              <Td className="font-medium">{r.name}</Td>
              <Td><a href={`mailto:${r.email}`} className="text-blue-600 hover:underline">{r.email}</a></Td>
              <Td>{r.phone}</Td>
              <Td className="text-xs">{r.role}</Td>
              <Td className="text-xs"><LicensePills licenses={r.licenses} /></Td>
              <Td className="text-xs">{r.provinces}</Td>
              <Td><StatusDot value={r.active === 'Yes'} /></Td>
              <Td className="text-xs text-gray-500">{r.last_login ? new Date(r.last_login).toLocaleDateString('en-CA') : '—'}</Td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    </div>
  );
}

function LicensePills({ licenses = [] }) {
  const cls = { insurance: 'bg-emerald-100 text-emerald-700', mortgage: 'bg-blue-100 text-blue-700', investments: 'bg-purple-100 text-purple-700' };
  return (
    <div className="flex gap-1 flex-wrap">
      {licenses.map(l => <span key={l} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls[l] || 'bg-gray-100 text-gray-700'}`}>{l}</span>)}
    </div>
  );
}

function StatusDot({ value }) {
  return <span className={`inline-block h-2 w-2 rounded-full ${value ? 'bg-emerald-500' : 'bg-gray-300'}`} />;
}

function NewAgentsTable({ rows = [], total = 0 }) {
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <Stat label="Total agents" value={total} color="indigo" />
        {rows.slice(-3).reverse().map(r => <Stat key={r.year} label={r.year} value={r.new_agents} color="blue" />)}
      </div>
      <ScrollTable testid="new-agents-by-year-table">
        <thead><tr><Th>Year</Th><Th>New agents</Th><Th>Names</Th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.year} className="hover:bg-gray-50">
              <Td className="font-medium">{r.year}</Td>
              <Td><strong>{r.new_agents}</strong></Td>
              <Td className="text-xs text-gray-600">{(r.agent_names || []).join(', ')}</Td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    </div>
  );
}

function GroupedAgentsTable({ summary = [], groupKey = 'license' }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {summary.map(s => <Stat key={s[groupKey]} label={s[groupKey]} value={s.agent_count} color="emerald" />)}
      </div>
      {summary.map(s => (
        <div key={s[groupKey]}>
          <h4 className="text-sm font-semibold text-gray-700 mt-2 capitalize">{s[groupKey]} <span className="text-xs text-gray-500">({s.agent_count})</span></h4>
          <ScrollTable>
            <thead><tr><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Active</Th></tr></thead>
            <tbody>
              {(s.agents || []).map((a, i) => (
                <tr key={`${s[groupKey]}-${i}`} className="hover:bg-gray-50">
                  <Td className="font-medium">{a.name}</Td>
                  <Td><a href={`mailto:${a.email}`} className="text-blue-600 hover:underline">{a.email}</a></Td>
                  <Td>{a.phone || '—'}</Td>
                  <Td><StatusDot value={a.active === 'Yes'} /></Td>
                </tr>
              ))}
            </tbody>
          </ScrollTable>
        </div>
      ))}
    </div>
  );
}

function AgentActivityTable({ counts = {}, rows = [] }) {
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        <Stat label="Never logged in" value={counts.never || 0} color="red" />
        <Stat label="Last 7 days" value={counts.active_7 || 0} color="emerald" />
        <Stat label="Last 30 days" value={counts.active_30 || 0} color="blue" />
        <Stat label="30–90 days" value={counts.stale_90 || 0} color="amber" />
        <Stat label="90+ days" value={counts.inactive || 0} color="red" />
      </div>
      <ScrollTable testid="agent-activity-table">
        <thead><tr><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Last login</Th><Th>Days ago</Th><Th>Status</Th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-gray-50">
              <Td className="font-medium">{r.name}</Td>
              <Td>{r.email}</Td>
              <Td>{r.phone || '—'}</Td>
              <Td>{r.last_login ? new Date(r.last_login).toLocaleDateString('en-CA') : '—'}</Td>
              <Td>{r.days_since_login === '' ? '—' : r.days_since_login}</Td>
              <Td className="text-xs">{r.status}</Td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    </div>
  );
}

function AgentDocsTable({ summary = {}, rows = [] }) {
  const statusCls = (s) => ({
    'Missing key docs': 'bg-red-100 text-red-700',
    'Expired docs':     'bg-red-100 text-red-700',
    'Renewal due':      'bg-amber-100 text-amber-700',
    'OK':               'bg-emerald-100 text-emerald-700',
  }[s] || 'bg-gray-100 text-gray-700');
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        <Stat label="Missing key docs" value={summary.agents_with_missing_docs || 0} color="red" />
        <Stat label="Expired docs" value={summary.agents_with_expired_docs || 0} color="red" />
        <Stat label="Renewal due (≤30d)" value={summary.agents_with_renewal_due_30d || 0} color="amber" />
      </div>
      <ScrollTable testid="agent-docs-table">
        <thead><tr><Th>Agent</Th><Th>Email</Th><Th>Phone</Th><Th>Docs</Th><Th>Missing</Th><Th>Expired</Th><Th>Expiring 30d</Th><Th>Status</Th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-gray-50">
              <Td className="font-medium">{r.name}</Td>
              <Td>{r.email}</Td>
              <Td>{r.phone || '—'}</Td>
              <Td>{r.doc_count}</Td>
              <Td className="text-xs">{(r.missing || []).join(', ') || '—'}</Td>
              <Td>{r.expired_docs}</Td>
              <Td>{r.expiring_within_30d}</Td>
              <Td><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusCls(r.compliance_status)}`}>{r.compliance_status}</span></Td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    </div>
  );
}

function ComplianceCasesTable({ per_agent = [], status_totals = {}, total = 0 }) {
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        <Stat label="Total cases" value={total} color="indigo" />
        {Object.entries(status_totals).map(([k, v]) => <Stat key={k} label={k} value={v} color="blue" />)}
      </div>
      <ScrollTable testid="compliance-cases-table">
        <thead><tr><Th>Agent</Th><Th>Total cases</Th><Th>By status</Th></tr></thead>
        <tbody>
          {per_agent.map((r, i) => (
            <tr key={i} className="hover:bg-gray-50">
              <Td className="font-medium">{r.agent_name}</Td>
              <Td><strong>{r.total}</strong></Td>
              <Td className="text-xs">{Object.entries(r.by_status || {}).map(([k, v]) => <span key={k} className="inline-block mr-2 bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{k}: {v}</span>)}</Td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    </div>
  );
}

function ClientFoldersTable({ totals = {}, rows = [] }) {
  return (
    <div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <Stat label="Total folders" value={totals.total_folders || 0} color="indigo" />
        <Stat label="Total files" value={totals.total_files || 0} color="blue" />
        <Stat label="Storage (MB)" value={totals.total_storage_mb || 0} color="emerald" />
      </div>
      <ScrollTable testid="client-folders-table">
        <thead><tr><Th>Agent</Th><Th>Folders</Th><Th>Files</Th><Th>Storage (MB)</Th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-gray-50">
              <Td className="font-medium">{r.agent_name}</Td>
              <Td>{r.folders}</Td>
              <Td>{r.files}</Td>
              <Td>{r.storage_mb}</Td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    </div>
  );
}

function PipelineSummary({ data = {}, kind = 'item' }) {
  const items = data.rows || [];
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <Stat label="Total" value={data.total || 0} color="indigo" />
        {Object.entries(data.by_status || {}).slice(0, 3).map(([k, v]) => <Stat key={k} label={k} value={v} color="blue" />)}
      </div>
      {data.by_month && data.by_month.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-gray-600 mb-1">By month</p>
          <div className="flex flex-wrap gap-1">
            {data.by_month.map(m => <span key={m.month} className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs">{m.month || 'unknown'}: <b>{m.count}</b></span>)}
          </div>
        </div>
      )}
      {items.length > 0 && (
        <ScrollTable testid={`pipeline-${kind}-table`}>
          <thead><tr>
            <Th>Name</Th><Th>Email</Th><Th>Phone</Th>
            {kind === 'quote' && <Th>Type</Th>}
            {kind === 'inquiry' && <Th>Subject</Th>}
            {kind === 'referral' && <Th>Referrer</Th>}
            <Th>Status</Th><Th>Created</Th>
          </tr></thead>
          <tbody>
            {items.map((r, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <Td className="font-medium">{r.name || r.client_name}</Td>
                <Td>{r.email}</Td>
                <Td>{r.phone}</Td>
                {kind === 'quote' && <Td>{r.type}</Td>}
                {kind === 'inquiry' && <Td>{r.subject}</Td>}
                {kind === 'referral' && <Td>{r.referrer}</Td>}
                <Td className="text-xs">{r.status}</Td>
                <Td className="text-xs text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-CA') : '—'}</Td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      )}
      {items.length === 0 && <p className="text-sm text-gray-500 text-center py-4">No records yet.</p>}
    </div>
  );
}

function ReferrersTable({ rows = [], total_referrers = 0, total_referrals = 0 }) {
  return (
    <div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <Stat label="Total referrers" value={total_referrers} color="indigo" />
        <Stat label="Total referrals" value={total_referrals} color="blue" />
      </div>
      <ScrollTable testid="referrers-table">
        <thead><tr><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Company</Th><Th>Referrals</Th><Th>Active</Th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-gray-50">
              <Td className="font-medium">{r.name}</Td>
              <Td>{r.email}</Td>
              <Td>{r.phone}</Td>
              <Td>{r.company}</Td>
              <Td><strong>{r.referral_count}</strong></Td>
              <Td><StatusDot value={r.active === 'Yes'} /></Td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
      {rows.length === 0 && <p className="text-sm text-gray-500 text-center py-4">No referrers yet.</p>}
    </div>
  );
}

function NewsletterTable({ rows = [], total = 0, by_month = [] }) {
  return (
    <div>
      <div className="flex gap-2 mb-3 flex-wrap">
        <Stat label="Subscribers" value={total} color="indigo" />
        {by_month.slice(-3).map(m => <Stat key={m.month} label={m.month || 'unknown'} value={m.count} color="blue" />)}
      </div>
      <ScrollTable testid="newsletter-table">
        <thead><tr><Th>Email</Th><Th>Name</Th><Th>Active</Th><Th>Subscribed</Th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-gray-50">
              <Td>{r.email}</Td>
              <Td>{r.name}</Td>
              <Td><StatusDot value={r.active === 'Yes'} /></Td>
              <Td className="text-xs text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-CA') : '—'}</Td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    </div>
  );
}

function CalendarBroadcastsTable({ rows = [], by_audience = {}, by_month = [], total = 0 }) {
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <Stat label="Total events" value={total} color="indigo" />
        {Object.entries(by_audience).map(([k, v]) => <Stat key={k} label={k} value={v} color="blue" />)}
      </div>
      <ScrollTable testid="calendar-broadcasts-table">
        <thead><tr><Th>Title</Th><Th>Audience</Th><Th>Date</Th><Th>All day</Th><Th>Created</Th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-gray-50">
              <Td className="font-medium">{r.title}</Td>
              <Td className="capitalize">{r.audience}</Td>
              <Td>{r.start_date}</Td>
              <Td>{r.all_day}</Td>
              <Td className="text-xs text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-CA') : '—'}</Td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
      {rows.length === 0 && <p className="text-sm text-gray-500 text-center py-4">No company events yet.</p>}
    </div>
  );
}

function ContentReport({ data }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Blog total" value={data.blog?.total || 0} color="indigo" />
        <Stat label="Blog published" value={data.blog?.published || 0} color="emerald" />
        <Stat label="Testimonials total" value={data.testimonials?.total || 0} color="indigo" />
        <Stat label="Testimonials published" value={data.testimonials?.published || 0} color="emerald" />
      </div>
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Blog Posts</h4>
        <ScrollTable testid="content-blog-table">
          <thead><tr><Th>Title</Th><Th>Author</Th><Th>Published</Th><Th>Tags</Th><Th>Created</Th></tr></thead>
          <tbody>
            {(data.blog?.rows || []).map((r, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <Td className="font-medium">{r.title}</Td>
                <Td>{r.author}</Td>
                <Td>{r.published}</Td>
                <Td className="text-xs">{(r.tags || []).join(', ')}</Td>
                <Td className="text-xs text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-CA') : '—'}</Td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Testimonials</h4>
        <ScrollTable testid="content-testimonials-table">
          <thead><tr><Th>Client</Th><Th>Rating</Th><Th>Published</Th><Th>Created</Th></tr></thead>
          <tbody>
            {(data.testimonials?.rows || []).map((r, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <Td className="font-medium">{r.client_name}</Td>
                <Td>{r.rating}</Td>
                <Td>{r.published}</Td>
                <Td className="text-xs text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-CA') : '—'}</Td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      </div>
    </div>
  );
}
